// File: ComboPage.tsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useCart } from "../Componants/Header/CartContext";
import { Helmet } from 'react-helmet-async';
import Modal from "react-modal";

// Setup Modal for accessibility
Modal.setAppElement("#root");

const API_BASE_URL = 'https://ayushvatika.com';
const IMAGE_BASE_URL = 'https://ayushvatika.com/images/products/';

const customStyles = `
  @keyframes marquee {
    0% { transform: translateX(100%); }
    100% { transform: translateX(-100%); }
  }
  .animate-slow-marquee {
    display: inline-block;
    animation: marquee 10s linear infinite;
    will-change: transform;
  }
`;

const ComboPage = () => {
    const navigate = useNavigate();
    const { addToCart } = useCart();
    const [products, setProducts] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Modal States
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedCombo, setSelectedCombo] = useState<any>(null);

    // Slider States
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [textKey, setTextKey] = useState(0); 
    const sliderRef = useRef<HTMLDivElement>(null); 
    const [sliderInView, setSliderInView] = useState(false); 

    // Fetch Products & Filter Only Combos
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/products`);
                const data = await response.json();
                
                const formattedProducts = data.map((p: any) => {
                    let imgPath = "default.png";
                    if (p.imageUrl) {
                        const list = p.imageUrl.split(',');
                        imgPath = list[0].trim().replace(/[\[\]"]/g, '');
                    }
                    
                    const discountPercent = p.discount || 50; 
                    const originalPrice = p.mrp || p.originalPrice || Math.round(p.price / (1 - discountPercent / 100));
                    const offerText = p.offer ? p.offer.toString() : ""; 
                    
                    let buyCount = 1; 
                    let getCount = 0; 
                    const lowerOffer = offerText.toLowerCase();

                    const buyMatch = lowerOffer.match(/(?:buy|b|but)\s*(\d+)/);
                    if (buyMatch && buyMatch[1]) buyCount = parseInt(buyMatch[1], 10);

                    const getMatch = lowerOffer.match(/(?:get|g)\s*(\d+)/);
                    if (getMatch && getMatch[1]) {
                        getCount = parseInt(getMatch[1], 10);
                    } else if (lowerOffer.includes("get one")) {
                        getCount = 1;
                    } else if (lowerOffer.includes("get two")) {
                        getCount = 2;
                    }

                    return {
                        id: p.id,
                        title: p.productName,
                        img: `${IMAGE_BASE_URL}${imgPath}`,
                        price: p.price,
                        oldPrice: originalPrice,
                        discount: discountPercent,
                        offer: offerText,
                        buyCount: buyCount,   
                        getCount: getCount,   
                        reviews: p.rating ? Math.floor(p.rating * 100) : Math.floor(Math.random() * 500) + 100,
                        seller: "AyushVatika"
                    };
                });
                
                const onlyCombos = formattedProducts.filter((p: any) => p.getCount > 0);
                setProducts(onlyCombos);

            } catch (e) {
                console.error("Error fetching combo products:", e);
                toast.error("Failed to load products");
            } finally {
                setIsLoading(false);
            }
        };

        fetchProducts();
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setSliderInView(true);
                    observer.disconnect(); 
                }
            },
            { root: null, rootMargin: '0px', threshold: 0.1 }
        );
        if (sliderRef.current) observer.observe(sliderRef.current);
        return () => { if (sliderRef.current) observer.unobserve(sliderRef.current); };
    }, []);

    const handleAddToCart = (product: any) => {
        // NAYA LOGIC: Naam ke saath Combo tag attach kar diya taaki cart ise pehchan sake
        const cartProductName = product.offer 
            ? `${product.title} (Combo: ${product.offer})` 
            : product.title;

        addToCart({
            productId: product.id,
            productName: cartProductName,
            price: product.price,
            imageUrl: product.img,
            seller: product.seller
        }, 1);
        toast.success(`Combo Offer added to cart!`);
    };

    const openComboModal = (product: any) => {
        setSelectedCombo(product);
        setModalOpen(true);
    };

    const closeComboModal = () => {
        setModalOpen(false);
        setSelectedCombo(null);
    };

    const handleGoToDetailsPage = (productId: number) => {
        closeComboModal();
        navigate(`/product/${productId}`);
    };

    if (isLoading) return <div className="p-20 text-center font-bold text-green-700">Loading Combos...</div>;

    return (
        <div className="bg-gray-50 py-6 sm:py-10 px-3 sm:px-8 md:px-12 min-h-screen">
            <style>{customStyles}</style>
            <Helmet>
                <title>Exclusive Ayurvedic Combos & Offers | Ayush Vatika</title>
                <meta name="description" content="Shop exclusive Ayurvedic combo packs." />
            </Helmet>

            <div className="flex justify-between items-center mb-6 sm:mb-8">
                <h1 className="text-xl sm:text-3xl font-semibold text-gray-800 border-b-4 border-green-700 pb-1">
                    Exclusive <span className="text-green-700">Combos & Offers</span>
                </h1>
            </div>

            {products.length === 0 ? (
                <div className="text-center py-20">
                    <h2 className="text-2xl font-bold text-gray-400">No active combo offers at the moment!</h2>
                    <p className="text-gray-500 mt-2">Please check back later.</p>
                </div>
            ) : (
                /* Yahan maine Grid ko 3 columns mein fix kiya hai (md:grid-cols-3 lg:grid-cols-3) */
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 sm:gap-8 max-w-7xl mx-auto">
                    {products.map(product => (
                        <div
                            key={product.id}
                            className="bg-white p-3 sm:p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col relative border border-gray-100 group"
                        >
                            {/* <div className="absolute top-4 left-0 bg-red-600 text-white font-bold text-[10px] sm:text-xs px-3 py-1 rounded-r-full shadow-md z-20">
                                {product.discount}% OFF
                            </div> */}

                            <div 
                                className="relative w-full h-[180px] sm:h-[260px] rounded-lg mb-4 overflow-hidden cursor-pointer border border-gray-100"
                                onClick={() => openComboModal(product)}
                            >
                                <div className="flex w-full h-full relative bg-gray-50">
                                    
                                    {/* Left Item (Buy) */}
                                    <div className="w-1/2 h-full relative border-r border-white">
                                        <img src={product.img} alt={product.title} className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500" />
                                        
                                        {/* Floating Label */}
                                        <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] sm:text-[11px] font-bold text-gray-800 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded shadow-md whitespace-nowrap z-10">
                                            {product.buyCount}x Item
                                        </span>
                                    </div>

                                    {/* Center Floating Plus Sign */}
                                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full shadow-lg flex items-center justify-center z-20">
                                        <span className="text-xl sm:text-2xl font-black text-green-600">+</span>
                                    </div>

                                    {/* Right Item (Free) */}
                                    <div className="w-1/2 h-full relative border-l border-white">
                                        <img src={product.img} alt={product.title} className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500" />
                                        
                                        {/* Floating Label */}
                                        <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] sm:text-[11px] font-bold text-green-800 bg-green-50/95 backdrop-blur-sm border border-green-200 px-2.5 py-1 rounded shadow-md whitespace-nowrap z-10">
                                            {product.getCount}x Free
                                        </span>
                                    </div>

                                </div>
                            </div>

                            <div className="text-center flex-grow flex flex-col justify-between">
                                <div className="mb-4">
                                    <h3 
                                        className="text-sm sm:text-lg font-bold text-gray-800 mb-2 leading-tight line-clamp-2 cursor-pointer hover:text-green-700"
                                        onClick={() => openComboModal(product)}
                                    >
                                        {product.title}
                                    </h3>
                                    
                                    <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-3">
                                        <span className="line-through text-gray-400 text-xs sm:text-sm">₹{product.oldPrice}</span>
                                        <span className="text-base sm:text-2xl font-black text-green-700">₹{product.price}</span>
                                    </div>

                                    {product.offer && (
                                        <div className="w-full overflow-hidden h-6 mb-2 bg-yellow-50 flex items-center rounded border border-yellow-200">
                                            <p className="text-[10px] sm:text-xs text-yellow-700 font-bold whitespace-nowrap animate-slow-marquee px-2">
                                                🎁 SPECIAL COMBO: {product.offer} 🎁
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col sm:flex-row gap-2 mt-auto">
                                    <button
                                        onClick={() => openComboModal(product)}
                                        className="flex-1 bg-white border-2 border-green-800 text-green-800 py-2 sm:py-2.5 rounded-lg hover:bg-green-50 transition text-xs sm:text-sm font-bold uppercase"
                                    >
                                        View Product
                                    </button>
                                    <button
                                        onClick={() => handleAddToCart(product)}
                                        className="flex-1 bg-green-800 text-white py-2 sm:py-2.5 rounded-lg hover:bg-green-900 transition shadow-md text-xs sm:text-sm font-bold uppercase"
                                    >
                                        Add to Bag
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* --- MODAL POPUP FOR COMBO DETAILS --- */}
            <Modal
                isOpen={modalOpen}
                onRequestClose={closeComboModal}
                className="relative w-[95%] sm:w-[600px] max-w-2xl mx-auto my-10 sm:my-20 bg-white rounded-2xl shadow-2xl p-6 sm:p-8 outline-none"
                overlayClassName="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 backdrop-blur-sm"
            >
                {selectedCombo && (
                    <div className="flex flex-col items-center text-center">
                        <button onClick={closeComboModal} className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="bg-red-100 text-red-700 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3">
                            Limited Time Offer
                        </div>

                        <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-2 leading-tight">
                            {selectedCombo.offer}
                        </h2>
                        <p className="text-gray-500 text-sm mb-6 font-medium">{selectedCombo.title}</p>
                        
                        <div className="flex items-center justify-center gap-2 sm:gap-4 w-full h-[180px] sm:h-[240px] mb-8 relative">
                            <div className="relative w-[45%] h-full flex flex-col items-center justify-center">
                                <img src={selectedCombo.img} alt="Product 1" className="w-full h-full object-contain drop-shadow-xl" />
                                <span className="absolute -bottom-4 text-xs sm:text-sm font-extrabold text-gray-700 bg-white border border-gray-200 px-3 py-1 rounded-full shadow-sm">{selectedCombo.buyCount}x Item</span>
                            </div>

                            <div className="text-4xl sm:text-6xl font-black text-green-600 drop-shadow-md z-10">+</div>

                            <div className="relative w-[45%] h-full flex flex-col items-center justify-center">
                                <div className="absolute top-0 right-0 sm:-right-4 bg-green-600 text-white text-[10px] sm:text-sm font-bold px-3 py-1 rounded-full animate-bounce z-20 shadow-lg">
                                    FREE
                                </div>
                                <img src={selectedCombo.img} alt="Product 2" className="w-full h-full object-contain drop-shadow-xl" />
                                <span className="absolute -bottom-4 text-xs sm:text-sm font-extrabold text-green-800 bg-green-50 border border-green-200 px-3 py-1 rounded-full shadow-sm">{selectedCombo.getCount}x Extra</span>
                            </div>
                        </div>

                        <div className="flex flex-col items-center mb-6 w-full mt-4">
                            <div className="flex items-center justify-center gap-3">
                                <span className="text-sm text-gray-400 line-through">Total Value: ₹{selectedCombo.oldPrice}</span>
                                <span className="text-2xl sm:text-3xl font-black text-green-700">You Pay: ₹{selectedCombo.price}</span>
                            </div>
                            <p className="text-sm sm:text-base text-green-600 font-bold mt-1">You Save {selectedCombo.discount}% Instantly!</p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 w-full mt-2">
                            <button
                                className="w-full bg-white border-2 border-green-800 text-green-800 py-3 rounded-xl font-bold text-sm sm:text-base hover:bg-green-50 transition-colors uppercase"
                                onClick={() => handleGoToDetailsPage(selectedCombo.id)}
                            >
                                View Full Details
                            </button>
                            <button
                                className="w-full bg-green-800 text-white py-3 rounded-xl font-bold text-sm sm:text-base hover:bg-green-900 transition-colors shadow-lg uppercase"
                                onClick={() => {
                                    handleAddToCart(selectedCombo);
                                    closeComboModal();
                                }}
                            >
                                Add Offer to Bag
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

        </div>
    );
};

export default ComboPage;